import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "../typography";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { DocumentStatus, getDocumentStatusInfo } from "../../utility/application-data";
import { getCandidateData } from "../../utility/candidate-data";
import { StatusEnum } from "../../hoc/with-status";
import { Button, ButtonSize, ButtonVariant } from "@amzn/stencil-react-components/button";
import { getLocale } from "../../utility/locale-helper";
import useArb from "../../hooks/use-arb";
import { addEventMetricWithBBId } from "../../utility/adobe-analytics";
import { View } from "@amzn/stencil-react-components/layout";

export interface UrlParams {
  applicationId: string;
}

export interface DocumentStatusTaskParams {
  status: StatusEnum;
  href: string;
}

export enum DocumentStatusButtonState {
  Active = "Active",
  Hidden = "Hidden",
  Disabled = "Disabled",
}

const ONBASE_COMPLETION_STATUS_COMPLETE = "Complete";
const ONBASE_COMPLETION_STATUS_PENDING = "Pending";
const ONBASE_COMPLETION_STATUS_ERROR = "Error";
const ONBASE_CANDIDATE_NOT_FOUND = "candidateNotFound";
const ADOBE_EVENT_START_EMPLOYMENT_PAPERWORK = "Start Employment Paperwork";
const ADOBE_EVENT_VIEW_EMPLOYMENT_PAPERWORK = "View Employment Paperwork";

export const DocumentStatusTask = ({ status, href }: DocumentStatusTaskParams) => {
  const [loading, setLoading] = useState(false);

  const bundle = useArb("HVHChecklistPantherResource");

  const loadingText = bundle.getMessage("Checklist-MainPage-Document-Status-Loading");

  const documentStatusPending = bundle.getMessage("Checklist-MainPage-Document-Status-Pending");

  const documentStatusComplete = bundle.getMessage("Checklist-MainPage-Document-Status-Complete");

  const documentStatusNotStartedYet = bundle.getMessage("Checklist-MainPage-Document-Status-Not-Started-Yet");

  const viewEmploymentPaperworkButton = bundle.getMessage(
    "Checklist-MainPage-Document-Status-View-Employment-Paperwork"
  );

  const startEmploymentPaperworkButton = bundle.getMessage(
    "Checklist-MainPage-Document-Status-Start-Employment-Paperwork"
  );

  const documentStatusError = bundle.getMessage("Checklist-MainPage-Document-Status-Error");

  const { applicationId } = useParams<UrlParams>();

  const locale = getLocale();

  const [documentStatusLoginUrl, setDocumentStatusLoginUrl] = useState<string>();

  const [documentCompletionStatus, setDocumentCompletionStatus] = useState<string>();

  const [documentButtonStatus, setDocumentButtonStatus] = useState<DocumentStatusButtonState>(
    DocumentStatusButtonState.Active
  );

  const requestDocumentStatusLink = useCallback(async () => {
    setLoading(true);
    setDocumentButtonStatus(DocumentStatusButtonState.Disabled);
    const candidateData = await getCandidateData(applicationId);

    let response = {} as DocumentStatus;
    if (candidateData && candidateData.candidateGlobalId) {
      try {
        response = await getDocumentStatusInfo(href, candidateData.candidateGlobalId, applicationId, locale);
      } catch (e) {
        setDocumentCompletionStatus(ONBASE_COMPLETION_STATUS_ERROR);
      }

      setDocumentButtonStatus(DocumentStatusButtonState.Hidden);
      if (response && response.results) {
        const results = response.results;
        results.forEach((item) => {
          if (item.onbaseLoginUrl && !item.error) {
            setDocumentCompletionStatus(item.documentCompletionStatus);
            setDocumentStatusLoginUrl(item.onbaseLoginUrl);
            setDocumentButtonStatus(DocumentStatusButtonState.Active);
          } else {
            if (item.error === ONBASE_CANDIDATE_NOT_FOUND) {
              setDocumentCompletionStatus(item.documentCompletionStatus);
            } else {
              setDocumentCompletionStatus(ONBASE_COMPLETION_STATUS_ERROR);
            }
          }
        });
      }
    }

    setLoading(false);
  }, []);

  const sendAnalyticsEvent = useCallback(
    async (eventName: string) => {
      await addEventMetricWithBBId(window, applicationId, eventName);
    },
    [applicationId]
  );

  const trackDocumentStatusClick = useCallback(() => sendAnalyticsEvent("Check Document Status"), [sendAnalyticsEvent]);

  useEffect(() => {
    requestDocumentStatusLink();
  }, []);

  const redirect = useCallback(() => {
    if (!documentStatusLoginUrl) {
      return;
    }

    if (documentCompletionStatus === ONBASE_COMPLETION_STATUS_COMPLETE) {
      sendAnalyticsEvent(ADOBE_EVENT_VIEW_EMPLOYMENT_PAPERWORK);
    } else if (documentCompletionStatus === ONBASE_COMPLETION_STATUS_PENDING) {
      sendAnalyticsEvent(ADOBE_EVENT_START_EMPLOYMENT_PAPERWORK);
    }

    window.open(documentStatusLoginUrl, "_blank");
  }, [documentStatusLoginUrl, trackDocumentStatusClick]);

  const isButtonDisabled = documentButtonStatus === DocumentStatusButtonState.Disabled;

  let documentStatusButtonTile = startEmploymentPaperworkButton;
  let documentStatusMessage = documentStatusNotStartedYet;

  if (documentCompletionStatus === ONBASE_COMPLETION_STATUS_COMPLETE) {
    documentStatusMessage = documentStatusComplete;
    documentStatusButtonTile = viewEmploymentPaperworkButton;
  } else if (documentCompletionStatus === ONBASE_COMPLETION_STATUS_PENDING) {
    documentStatusMessage = documentStatusPending;
  } else if (documentCompletionStatus === ONBASE_COMPLETION_STATUS_ERROR) {
    documentStatusMessage = documentStatusError;
  }

  const shouldShowButton =
    documentButtonStatus === DocumentStatusButtonState.Active ||
    status === StatusEnum.API_ERROR ||
    status === StatusEnum.COMPLETED ||
    status === StatusEnum.INELIGIBLE;

  if (loading) {
    return <Spinner size={SpinnerSize.Small} loadingText={loadingText} showText={true}></Spinner>;
  }

  return (
    <Text>
      {documentStatusMessage}
      <br />
      <View margin="4px">
        <br />
        {shouldShowButton && (
          <Button
            size={ButtonSize.Small}
            variant={ButtonVariant.Primary}
            onClick={redirect}
            disabled={isButtonDisabled}
          >
            {documentStatusButtonTile}
          </Button>
        )}
      </View>
    </Text>
  );
};
